import React from "react";
import { Button, Tooltip } from "antd";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import keycloak, { loginToApp } from "../keycloak";
import { useAuthStatus } from "../auth/AuthStatus";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { TrustStrip } from "../components/TrustStrip";
import { RoiCalculator } from "./RoiCalculator";
import { WelcomeSeo } from "./WelcomeSeo";

const FEATURE_KEYS = ["eudrReady", "riskBased", "auditReady", "shareable"] as const;
const FEATURE_ICONS = ["🛡", "◎", "▤", "●"] as const;

const JOURNEY_KEYS = [
  "deal",
  "case",
  "evidence",
  "validate",
  "gaps",
  "passport",
  "share",
] as const;
const JOURNEY_ICONS = ["🤝", "▰", "☁", "◇", "☷", "▣", "➤"] as const;

const PROVIDER_KEYS = ["stub", "gee", "gfw", "copernicus", "nicfi", "jrc", "mapbiomas"] as const;
const PROVIDER_ICONS = ["</>", "⬢", "🌳", "◯", "◎", "🇪🇺", "◆"] as const;

const EVIDENCE_KEYS = ["origin", "supplier", "documents", "transport", "risk"] as const;

/** Regulation (EU) 2023/1115 — language-aware EUR-Lex when possible. */
export function eudrRegulationHref(lang: string): string {
  const code = (lang || "en").split("-")[0].toLowerCase();
  const supported = new Set([
    "bg",
    "cs",
    "da",
    "de",
    "el",
    "en",
    "es",
    "et",
    "fi",
    "fr",
    "ga",
    "hr",
    "hu",
    "it",
    "lt",
    "lv",
    "mt",
    "nl",
    "pl",
    "pt",
    "ro",
    "sk",
    "sl",
    "sv",
  ]);
  const pathLang = supported.has(code) ? code : "en";
  return `https://eur-lex.europa.eu/legal-content/${pathLang.toUpperCase()}/TXT/?uri=CELEX%3A32023R1115`;
}

function EudrLink({
  href,
  testId,
  className = "welcome-eudr-link",
  children,
}: {
  href: string;
  testId?: string;
  className?: string;
  children?: React.ReactNode;
}) {
  return (
    <a
      className={className}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
    >
      {children}
    </a>
  );
}

/** First-mention DDS gloss: tooltip definition + link to EUR-Lex Regulation (EU) 2023/1115. */
function DdsGlossLink({ href, children }: { href: string; children?: React.ReactNode }) {
  const { t } = useTranslation();
  const title = (
    <span className="welcome-dds-tooltip">
      <strong>{t("investor.welcome.ddsGloss.title")}</strong>
      <br />
      {t("investor.welcome.ddsGloss.body")}
      <br />
      <a href={href} target="_blank" rel="noopener noreferrer">
        {t("investor.welcome.ddsGloss.linkLabel")}
      </a>
    </span>
  );
  return (
    <Tooltip title={title} overlayClassName="welcome-dds-tooltip-overlay">
      <a
        className="welcome-eudr-link welcome-dds-link"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="welcome-dds-gloss"
      >
        {children ?? t("investor.welcome.ddsGloss.label")}
      </a>
    </Tooltip>
  );
}

/** Public landing — ported from docs/landing-archive/eudora_eu_style_landing.html (dark EU blue). */
export function WelcomePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { authReady } = useAuthStatus();
  const authenticated = Boolean(authReady && keycloak.authenticated);
  const eudrHref = eudrRegulationHref(i18n.language);

  const goLogin = () => {
    if (authenticated) {
      navigate("/dds");
      return;
    }
    loginToApp("/dds");
  };

  /** Primary CTA — Keycloak then create / investor-demo passport path (US-806). */
  const goCreatePassport = () => {
    if (authenticated) {
      navigate("/dds?investorDemo=1");
      return;
    }
    loginToApp("/dds?investorDemo=1");
  };

  const loginLabel = authenticated ? t("investor.welcome.openApp") : t("investor.welcome.login");
  const ctaLabel = t("investor.welcome.createPassport");

  const scrollTo = (id: string) => (event: React.MouseEvent) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="welcome-page" data-testid="welcome-page">
      <WelcomeSeo />
      <div className="welcome-topbar">
        <div className="welcome-wrap welcome-topbar__inner">
          <div className="welcome-eu-mini">
            <span className="welcome-flag" aria-hidden>
              ✦
            </span>
            <span>{t("investor.welcome.strip")}</span>
          </div>
          <div className="welcome-topbar__right">
            <span>{t("investor.welcome.topLinks")}</span>
            <LanguageSwitcher />
          </div>
        </div>
      </div>

      <nav className="welcome-nav" aria-label={t("investor.welcome.heroAria")}>
        <div className="welcome-wrap welcome-nav__inner">
          <div className="welcome-brand-box">
            <h1 className="welcome-brand">
              <a href="#product" onClick={scrollTo("product")}>
                {t("app.brand")}
              </a>
            </h1>
            <div className="welcome-brand-note">
              <Trans
                i18nKey="investor.welcome.brandNote"
                components={{ b: <b /> }}
              />
            </div>
          </div>
          <div className="welcome-navlinks">
            <a href="#product" onClick={scrollTo("product")}>
              {t("investor.welcome.nav.product")}
            </a>
            <a href="#journey" onClick={scrollTo("journey")}>
              {t("investor.welcome.nav.solutions")}
            </a>
            <a href="#journey" onClick={scrollTo("journey")}>
              {t("investor.welcome.nav.useCases")}
            </a>
            <a href="#providers" onClick={scrollTo("providers")}>
              {t("investor.welcome.nav.providers")}
            </a>
            <a href="#pricing" onClick={scrollTo("pricing")}>
              {t("investor.welcome.nav.pricing")}
            </a>
          </div>
          <div className="welcome-nav__actions">
            <Button type="primary" onClick={goCreatePassport} data-testid="welcome-try-demo">
              {ctaLabel}
            </Button>
          </div>
        </div>
      </nav>

      <header className="welcome-hero" id="product" aria-label={t("investor.welcome.heroAria")}>
        <div className="welcome-wrap">
          <div className="welcome-hero-grid">
            <div>
              <p className="welcome-hero__headline">
                <Trans
                  i18nKey="investor.welcome.headline"
                  components={{
                    eudr: <EudrLink href={eudrHref} testId="welcome-hero-eudr-regulation-link" />,
                    accent: <span className="welcome-accent" />,
                    br: <br />,
                  }}
                />
              </p>
              <p className="welcome-lead">
                <Trans
                  i18nKey="investor.welcome.lead"
                  components={{
                    eudr: <EudrLink href={eudrHref} testId="welcome-eudr-regulation-link" />,
                    dds: <DdsGlossLink href={eudrHref} />,
                  }}
                />
              </p>
              <p className="welcome-dds-note" data-testid="welcome-dds-note">
                <Trans
                  i18nKey="investor.welcome.ddsNote"
                  components={{
                    dds: <DdsGlossLink href={eudrHref} />,
                    eudr: <EudrLink href={eudrHref} />,
                  }}
                />
              </p>
              <div className="welcome-hero-actions">
                <Button type="primary" size="large" onClick={goCreatePassport} data-testid="welcome-hero-create">
                  {t("investor.welcome.createPassportHero")}
                </Button>
                <Button size="large" ghost className="welcome-btn-ghost" href="#journey" onClick={scrollTo("journey")}>
                  {t("investor.welcome.seeHow")}
                </Button>
                <Button size="large" ghost className="welcome-btn-ghost" onClick={goLogin} data-testid="welcome-login">
                  {loginLabel}
                </Button>
              </div>
              <div className="welcome-reg-row">
                <Trans
                  i18nKey="investor.welcome.regRow"
                  components={{
                    eudr: <EudrLink href={eudrHref} />,
                    b: <b />,
                  }}
                />
              </div>
            </div>

            <div
              className="welcome-passport-visual"
              data-testid="welcome-passport-visual"
              aria-label={t("investor.welcome.passportVisualAria")}
            >
              <div className="welcome-globe" aria-hidden />
              <div className="welcome-passport-card">
                <div className="welcome-passport-card__title">{t("app.brand")}</div>
                <div className="welcome-passport-card__stars" aria-hidden>
                  ★ ★ ★
                </div>
                <strong>
                  <DdsGlossLink href={eudrHref}>{t("investor.welcome.passportCardTitle")}</DdsGlossLink>
                </strong>
                <small>
                  <Trans
                    i18nKey="investor.welcome.passportCardSub"
                    components={{
                      eudr: <EudrLink href={eudrHref} />,
                      br: <br />,
                    }}
                  />
                </small>
              </div>
            </div>
          </div>

          <div className="welcome-feature-strip" data-testid="welcome-features">
            {FEATURE_KEYS.map((key, index) => (
              <div key={key} className="welcome-feature">
                <div className="welcome-icon" aria-hidden>
                  {FEATURE_ICONS[index]}
                </div>
                <h3>
                  {key === "eudrReady" ? (
                    <Trans
                      i18nKey={`investor.welcome.features.${key}.title`}
                      components={{ eudr: <EudrLink href={eudrHref} /> }}
                    />
                  ) : (
                    t(`investor.welcome.features.${key}.title`)
                  )}
                </h3>
                <p>{t(`investor.welcome.features.${key}.body`)}</p>
              </div>
            ))}
          </div>
        </div>
      </header>

      <section className="welcome-section" id="journey" data-testid="welcome-how">
        <div className="welcome-wrap">
          <div className="welcome-section-title">
            <h2>
              <Trans
                i18nKey="investor.welcome.journey.title"
                components={{ eudr: <EudrLink href={eudrHref} /> }}
              />
            </h2>
            <div className="welcome-under" aria-hidden />
          </div>
          <div className="welcome-journey">
            {JOURNEY_KEYS.map((key, index) => (
              <div key={key} className="welcome-step welcome-how__step">
                <div className="welcome-step-no">{index + 1}</div>
                <div className="welcome-icon" aria-hidden>
                  {JOURNEY_ICONS[index]}
                </div>
                <h3>{t(`investor.welcome.journey.steps.${key}.title`)}</h3>
                <p>
                  {key === "passport" ? (
                    <Trans
                      i18nKey={`investor.welcome.journey.steps.${key}.body`}
                      components={{ eudr: <EudrLink href={eudrHref} /> }}
                    />
                  ) : (
                    t(`investor.welcome.journey.steps.${key}.body`)
                  )}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="welcome-section" id="passport" data-testid="welcome-passport">
        <div className="welcome-wrap">
          <div className="welcome-passport-section">
            <div className="welcome-preview">
              <div className="welcome-preview-head">{t("investor.welcome.preview.head")}</div>
              <div className="welcome-risk-grid">
                <div className="welcome-risk-card">
                  <h4>{t("investor.welcome.preview.overallRisk")}</h4>
                  <div className="welcome-low">{t("investor.welcome.preview.lowRisk")}</div>
                  <div className="welcome-risk-lines">
                    <div>
                      {t("investor.welcome.preview.countryRisk")}{" "}
                      <span>{t("investor.welcome.preview.low")}</span>
                    </div>
                    <div>
                      {t("investor.welcome.preview.deforestationRisk")}{" "}
                      <span>{t("investor.welcome.preview.low")}</span>
                    </div>
                    <div>
                      {t("investor.welcome.preview.legalityRisk")}{" "}
                      <span>{t("investor.welcome.preview.low")}</span>
                    </div>
                  </div>
                </div>
                <div className="welcome-risk-card welcome-checklist">
                  <h4>{t("investor.welcome.preview.evidencePackage")}</h4>
                  {EVIDENCE_KEYS.map((key) => (
                    <div key={key}>
                      <span className="welcome-check" aria-hidden>
                        ✓
                      </span>
                      {t(`investor.welcome.preview.evidence.${key}`)}
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="welcome-passport-copy">
              <h2>
                <Trans
                  i18nKey="investor.welcome.passportCopy.title"
                  components={{ span: <span /> }}
                />
              </h2>
              <p>
                <Trans
                  i18nKey="investor.welcome.passportCopy.lead"
                  components={{ eudr: <EudrLink href={eudrHref} /> }}
                />
              </p>
              <ul>
                <li>{t("investor.welcome.passportCopy.bullets.evidence")}</li>
                <li>{t("investor.welcome.passportCopy.bullets.risk")}</li>
                <li>
                  <Trans
                    i18nKey="investor.welcome.passportCopy.bullets.aligned"
                    components={{ eudr: <EudrLink href={eudrHref} /> }}
                  />
                </li>
                <li>{t("investor.welcome.passportCopy.bullets.share")}</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="welcome-section" id="providers" data-testid="welcome-providers">
        <div className="welcome-wrap">
          <div className="welcome-section-title">
            <h2>{t("investor.welcome.providers.title")}</h2>
            <p>{t("investor.welcome.providers.lead")}</p>
          </div>
          <div className="welcome-providers">
            {PROVIDER_KEYS.map((key, index) => (
              <div key={key} className="welcome-provider welcome-providers__item">
                <div className="welcome-provider-icon" aria-hidden>
                  {PROVIDER_ICONS[index]}
                </div>
                <strong className="welcome-providers__label">
                  {t(`investor.welcome.providers.items.${key}.title`)}
                </strong>
                <p className="welcome-providers__body">
                  {t(`investor.welcome.providers.items.${key}.body`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <RoiCalculator />

      <section className="welcome-section" id="pricing" data-testid="welcome-start">
        <div className="welcome-wrap">
          <div className="welcome-cta">
            <div>
              <h2>
                <Trans
                  i18nKey="investor.welcome.cta.title"
                  components={{
                    eudr: <EudrLink href={eudrHref} />,
                    br: <br />,
                  }}
                />
              </h2>
              <p>{t("investor.welcome.cta.lead")}</p>
            </div>
            <div className="welcome-cta__actions">
              <Button
                type="primary"
                size="large"
                className="welcome-btn-yellow"
                onClick={goCreatePassport}
                data-testid="welcome-start-try-demo"
              >
                {ctaLabel}
              </Button>
              <Button size="large" ghost className="welcome-btn-ghost" onClick={goLogin} data-testid="welcome-start-login">
                {loginLabel}
              </Button>
              <Button
                size="large"
                ghost
                className="welcome-btn-ghost"
                onClick={goCreatePassport}
                data-testid="welcome-demo"
              >
                {t("investor.welcome.demo")}
              </Button>
            </div>
          </div>
          <p className="welcome-start__hint">{t("investor.welcome.start.hint")}</p>
        </div>
      </section>

      <section
        className="welcome-section welcome-honesty"
        data-testid="welcome-honesty"
        aria-labelledby="welcome-honesty-heading"
      >
        <div className="welcome-wrap">
          <h2 id="welcome-honesty-heading" className="welcome-section-title-inline">
            {t("investor.welcome.honesty.title")}
          </h2>
          <p className="welcome-section-lead-inline">{t("investor.welcome.honesty.lead")}</p>
          <TrustStrip className="welcome-page__trust" />
        </div>
      </section>

      <footer className="welcome-footer">
        <div className="welcome-wrap welcome-footer-grid">
          <div>
            <div className="welcome-foot-brand">{t("app.brand")}</div>
            <div className="welcome-foot-note">
              <Trans i18nKey="investor.welcome.brandNote" components={{ b: <b /> }} />
            </div>
            <p className="welcome-foot-tagline">{t("investor.welcome.footer.tagline")}</p>
          </div>
          <div>
            <h4>{t("investor.welcome.footer.product")}</h4>
            <a href="#product" onClick={scrollTo("product")}>
              {t("investor.welcome.footer.overview")}
            </a>
            <a href="#passport" onClick={scrollTo("passport")}>
              {t("investor.welcome.footer.features")}
            </a>
            <a href="#providers" onClick={scrollTo("providers")}>
              {t("investor.welcome.footer.security")}
            </a>
          </div>
          <div>
            <h4>{t("investor.welcome.footer.solutions")}</h4>
            <a href="#journey" onClick={scrollTo("journey")}>
              {t("investor.welcome.footer.timber")}
            </a>
            <a href="#journey" onClick={scrollTo("journey")}>
              {t("investor.welcome.footer.palm")}
            </a>
            <a href="#journey" onClick={scrollTo("journey")}>
              {t("investor.welcome.footer.other")}
            </a>
          </div>
          <div>
            <h4>{t("investor.welcome.footer.resources")}</h4>
            <a className="welcome-eudr-link" href={eudrHref} target="_blank" rel="noopener noreferrer">
              {t("investor.welcome.footer.regulation")}
            </a>
            <a href="#journey" onClick={scrollTo("journey")}>
              {t("investor.welcome.footer.guides")}
            </a>
            <a href="mailto:hello@eudora.ytitov.com">{t("investor.welcome.footer.help")}</a>
          </div>
          <div>
            <div className="welcome-flag" aria-hidden>
              ✦
            </div>
            <p>{t("investor.welcome.footer.aligned")}</p>
            <p>{t("investor.welcome.footer.copyright")}</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
